import path from 'path'
import type { CollectionConfig, Config, UploadCollectionSlug } from 'payload'
import { initClientUploads } from '@payloadcms/plugin-cloud-storage/utilities'
import type {
    Adapter,
    PluginOptions as CloudStoragePluginOptions,
    CollectionOptions as CloudStorageCollectionOptions,
    GeneratedAdapter,
} from '@payloadcms/plugin-cloud-storage/types'
import { cloudStoragePlugin } from '@payloadcms/plugin-cloud-storage'
import { wlodevCloudEnabled } from '../config.js'
import {
    getLocalUploadURL,
    getWlodevFilePath,
    getWlodevImageOptimizationPath,
} from './tools.js'
import { getGenerateSignedURLHandler } from './generateSignedURL.js'
import { getGenerateURL } from './generateURL.js'
import { getHandleUpload } from './handleUpload.js'
import { getHandleDelete } from './handleDelete.js'
import { getStaticHandler } from './staticHandler.js'

const DEFAULT_UPLOAD_COLLECTION: Partial<CollectionConfig>['upload'] = {
    crop: false,
    focalPoint: false,
}

type WlodevStorageOptions = {}

export const uploadPlugin =
    (uploadOptions?: WlodevStorageOptions) =>
    (incomingConfig: Config): Config => {
        let config = incomingConfig

        const enabledCollections = (incomingConfig.collections || []).reduce(
            (acc, collection) => {
                if (collection.upload) {
                    acc[collection.slug] = true
                }
                return acc
            },
            {} as Record<UploadCollectionSlug, boolean>
        )

        initClientUploads({
            clientHandler: '@wlodev/payload/client#WlodevClientUploadHandler',
            config,
            collections: enabledCollections,
            enabled: wlodevCloudEnabled,
            serverHandler: getGenerateSignedURLHandler({}),
            serverHandlerPath: '/storage-wlodev-client-uploads',
        })

        if (!wlodevCloudEnabled) {
            config = {
                ...config,
                collections: (config.collections || []).map(collection => {
                    if (!enabledCollections[collection.slug]) {
                        return collection
                    }

                    return {
                        ...collection,
                        hooks: {
                            ...(collection.hooks || {}),
                            afterRead: [
                                ...(collection.hooks?.afterRead || []),
                                async ({ doc, req }) => {
                                    if (doc.url && path.resolve(doc.url)) {
                                        const url = getLocalUploadURL(
                                            doc.url,
                                            req
                                        )
                                        if (url) {
                                            doc.url = url.toString()
                                        }
                                    }
                                    return doc
                                },
                            ],
                        },
                        upload: {
                            ...(typeof collection.upload !== 'boolean' &&
                            collection.upload
                                ? collection.upload
                                : {}),
                            ...DEFAULT_UPLOAD_COLLECTION,
                        },
                    }
                }),
            }
            return config
        }

        const adapter = wlodevStorageInternal(uploadOptions)
        const collectionsWithAdapter: CloudStoragePluginOptions['collections'] =
            Object.keys(enabledCollections).reduce((acc, collectionSlug) => {
                acc[collectionSlug] = {
                    adapter,
                    // ! This one makes the files accessible publicly
                    // It changes the "url" of file in the payloadcms api from the /<basepath>/api
                    // to one provided in the generateURL function
                    disablePayloadAccessControl: true,
                }
                return acc
            }, {} as Record<UploadCollectionSlug, CloudStorageCollectionOptions>)

        config = {
            ...config,
            collections: (config.collections || []).map(collection => {
                if (!enabledCollections[collection.slug]) {
                    return collection
                }

                return {
                    ...collection,
                    upload: {
                        ...(typeof collection.upload !== 'boolean' &&
                        collection.upload
                            ? collection.upload
                            : {}),
                        ...DEFAULT_UPLOAD_COLLECTION,
                        disableLocalStorage: true,
                        adminThumbnail: ({ doc }) => {
                            if (
                                doc &&
                                typeof doc.filename === 'string' &&
                                typeof doc.mimeType === 'string' &&
                                doc.mimeType.startsWith('image/')
                            ) {
                                // First prepare the image url
                                const imagePath = getWlodevFilePath(
                                    collection.slug,
                                    doc.filename
                                )

                                // Then prepare the optimization url which will show the image in small format
                                const optimizationPath =
                                    getWlodevImageOptimizationPath({
                                        imagePath,
                                        quality: 50,
                                        width: 320,
                                    })
                                return optimizationPath
                            }
                            return null
                        },
                    },
                }
            }),
        }

        return cloudStoragePlugin({
            collections: collectionsWithAdapter,
            enabled: true,
        })(config)
    }

const wlodevStorageInternal = (props?: WlodevStorageOptions): Adapter => {
    return ({ collection, prefix }): GeneratedAdapter => {
        return {
            name: 'wlodev',
            clientUploads: true,
            generateURL: getGenerateURL(),
            handleUpload: getHandleUpload({
                collection,
                prefix,
            }),
            handleDelete: getHandleDelete(),
            staticHandler: getStaticHandler(),
        }
    }
}
